import React, { useCallback, useEffect, useRef, useState } from "react";
import { StackScreenProps } from "@react-navigation/stack";
import { CompositeScreenProps } from "@react-navigation/core";
import { useLazyQuery } from "@apollo/client/react";
import { useAtom, useSetAtom } from "jotai";
import commonStyles from "../common-style";
import ScreenCard from "../ScreenCard";
import { useI18n } from "../../hooks/i18n-hooks";
import VStack from "../../ui_atoms/VStack";
import TextField from "../../components/TextField";
import Text from "../../components/Text";
import { BodyMediumText } from "../../components/FormattedText";
import { SignupSigninStackParamList } from "../../navigations/SignupSigninStack";
import { RootStackParamList } from "../../navigations/RootNavigation";
import { INVITATION_BY_CODE } from "../../graphql/invitation";
import { invitationCodeStepDone, pendingInvitationCode } from "../../atoms/auth";
import { useInvitationCodeErrorMessage } from "../../hooks/invitation-code";

type InvitationCodeScreenProps = CompositeScreenProps<
    StackScreenProps<SignupSigninStackParamList, "InvitationCode">,
    StackScreenProps<RootStackParamList>
>;

/**
 * Signup step for someone invited by email: the email carries a code (`XXXX-XXXX`) that finds the
 * invitation, which is then accepted or declined like one sent to the phone number. Opened by the
 * role selection when no invitation was sent to the phone number; prefilled (and submitted) with the
 * code of an invitation link opened before signing up.
 */
const InvitationCodeScreen = ({ navigation }: InvitationCodeScreenProps) => {
    const i18n = useI18n();
    const [storedCode, setStoredCode] = useAtom(pendingInvitationCode);
    const setStepDone = useSetAtom(invitationCodeStepDone);
    const [code, setCode] = useState(storedCode ?? "");
    const [errorMessage, setErrorMessage] = useState<string>();
    const [isChecking, setIsChecking] = useState(false);
    const errorMessageFor = useInvitationCodeErrorMessage();
    const [findInvitation] = useLazyQuery(INVITATION_BY_CODE, { fetchPolicy: "network-only" });

    // The step is behind the user from here: the role selection no longer sends them back to it.
    const leaveStep = useCallback(() => {
        setStepDone(true);
        setStoredCode(null);
    }, [setStepDone, setStoredCode]);

    const checkCode = useCallback(
        async (codeToCheck: string) => {
            const invitationCode = codeToCheck.trim();
            if (!invitationCode) {
                return;
            }
            setErrorMessage(undefined);
            setIsChecking(true);
            try {
                const { data } = await findInvitation({ variables: { code: invitationCode } });
                const result = data?.invitationByCode;
                if (result?.__typename === "InvitationCodeSuccess") {
                    leaveStep();
                    navigation.navigate("Invitation", { invitationCode });
                    // Leaving the invitation (accepted, declined or closed) comes back to the role
                    // selection, as for an invitation sent to the phone number.
                    navigation.popTo("SelectRole", undefined, { merge: true });
                } else {
                    setErrorMessage(errorMessageFor(result?.messageKey));
                }
            } catch {
                setErrorMessage(errorMessageFor());
            } finally {
                setIsChecking(false);
            }
        },
        [errorMessageFor, findInvitation, leaveStep, navigation],
    );

    const skip = useCallback(() => {
        leaveStep();
        navigation.popTo("SelectRole", undefined, { merge: true });
    }, [leaveStep, navigation]);

    // A code from an invitation link is checked once, without waiting for a tap. It may only come
    // from storage after the first render (an app restart mid-signup).
    const prefilledCodeChecked = useRef(false);
    useEffect(() => {
        if (storedCode && !prefilledCodeChecked.current) {
            prefilledCodeChecked.current = true;
            setCode(storedCode);
            void checkCode(storedCode);
        }
    }, [storedCode, checkCode]);

    const onChangeCode = useCallback((text: string) => {
        setCode(text.toUpperCase());
        setErrorMessage(undefined);
    }, []);

    return (
        <ScreenCard
            title={i18n.t("screens.auth.invitation_code.title")}
            buttons={[
                {
                    title: i18n.t("screens.auth.invitation_code.submit"),
                    onPress: () => void checkCode(code),
                    isValid: !!code.trim() && !isChecking,
                    validating: isChecking,
                    mode: "primary",
                    testID: "button-invitation-code-submit",
                },
                {
                    title: i18n.t("screens.auth.invitation_code.skip"),
                    onPress: skip,
                    isValid: !isChecking,
                    testID: "button-invitation-code-skip",
                },
            ]}
        >
            <VStack gap={1} v={0} h="2/3">
                <BodyMediumText>
                    {i18n.t("screens.auth.invitation_code.description")}
                </BodyMediumText>
                <TextField
                    testID="input-invitation-code"
                    value={code}
                    onChangeText={onChangeCode}
                    placeholder={i18n.t("screens.auth.invitation_code.placeholder")}
                    autoCapitalize="characters"
                    autoCorrect={false}
                    autoComplete="off"
                    returnKeyType="go"
                    onSubmitEditing={() => void checkCode(code)}
                    error={!!errorMessage}
                    disabled={isChecking}
                />
                <Text
                    style={[
                        commonStyles.screenErrorMessage,
                        { color: errorMessage ? "red" : "transparent" },
                    ]}
                >
                    {errorMessage}
                </Text>
            </VStack>
        </ScreenCard>
    );
};

export default InvitationCodeScreen;
